import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Container } from "@/components/site/Container";
import {
  getSiteSettings,
  updateSiteSettings,
  checkIsAdmin,
} from "@/lib/site-settings.functions";
import {
  connectGoogleSearchConsole,
  disconnectGoogleSearchConsole,
} from "@/lib/gsc.functions";

import { getAdminStats, getPagesNeedingPhotos } from "@/lib/admin-stats.functions";
import {
  listMediaAssets,
  createMediaAsset,
  updateMediaAsset,
  deleteMediaAsset,
  type MediaAsset,
} from "@/lib/media.functions";
import { listMediaUsage } from "@/lib/media-usage.functions";
import {
  getPageAssignments,
  upsertAssignment,
  clearAssignment,
  listAssignmentSlotsByPage,
} from "@/lib/image-assignments.functions";
import { regenerateAssignmentSeo, bulkCategorizeImages } from "@/lib/auto-images.functions";
import {
  listPageVerifications,
  setPageVerified,
} from "@/lib/page-verifications.functions";
import { ImageLightbox } from "@/components/admin/ImageLightbox";
import { TagPicker } from "@/components/admin/TagPicker";
import { SlotMediaPickerModal } from "@/components/admin/SlotMediaPickerModal";
import { uploadFilesToMedia } from "@/lib/media-upload";

import {
  PAGES,
  MEDIA_CATEGORIES,
  placeholderFor,
  type PageEntry,
  type ImageSlot,
} from "@/lib/pages";
import {
  Settings,
  Star,
  FileText,
  Image as ImageIcon,
  Cloud,
  Bot,
  Share2,
  Upload,
  Trash2,
  X,
  Check,
  CheckCircle2,
  Plug,
  BookOpen,
  Building2,
  Camera,
  Mic,
} from "lucide-react";
import { BlogModule } from "@/components/admin/BlogModule";
import { DraftsModule } from "@/components/admin/DraftsModule";
import { IntegrationsModule } from "@/components/admin/IntegrationsModule";
import { PagesModule } from "@/components/admin/PagesModule";
import { DriveSyncModule } from "@/components/admin/DriveSyncModule";
import { ContentGeneratorModule } from "@/components/admin/ContentGeneratorModule";
import { LeadsModule } from "@/components/admin/LeadsModule";
import { InterviewPostsModule } from "@/components/admin/InterviewPostsModule";
import { AutoImagesModule } from "@/components/admin/AutoImagesModule";
import { Inbox, Wand2, Sparkles, MessageCircle } from "lucide-react";
import { ChatQuestionsModule } from "@/components/admin/ChatQuestionsModule";
import { CalculatorModule } from "@/components/admin/CalculatorModule";
import { BookingsModule } from "@/components/admin/BookingsModule";
import { AnalyticsModule } from "@/components/admin/AnalyticsModule";
import { Calculator, CalendarCheck, BarChart3 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminDashboard,
  head: () => ({
    meta: [
      { title: "Admin Dashboard | Durafoam Roofing" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

const MODULES = [
  { key: "overview", label: "Dashboard", icon: Settings, available: true },
  { key: "pages", label: "Pages (CMS)", icon: FileText, available: true },
  { key: "leads", label: "Leads", icon: Inbox, available: true },
  { key: "chatQuestions", label: "Chat Questions", icon: MessageCircle, available: true },
  { key: "calculator", label: "Calculator", icon: Calculator, available: true },
  { key: "bookings", label: "Bookings", icon: CalendarCheck, available: true },
  { key: "analytics", label: "Analytics", icon: BarChart3, available: true },
  { key: "blog", label: "Blog Posts", icon: BookOpen, available: true },
  { key: "interviews", label: "Interview Posts", icon: Mic, available: true },
  { key: "generator", label: "Content Generator", icon: Bot, available: true },
  { key: "gbp", label: "GBP Drafts", icon: Building2, available: true },
  { key: "social", label: "Social Drafts", icon: Share2, available: true },
  { key: "stories", label: "Story Drafts", icon: Camera, available: true },
  { key: "media", label: "Media Library", icon: ImageIcon, available: true },
  { key: "pageImages", label: "Page Images", icon: ImageIcon, available: true },
  { key: "autoImages", label: "Auto Images (AI)", icon: Wand2, available: true },
  { key: "drive", label: "Google Drive Sync", icon: Cloud, available: true },
  { key: "integrations", label: "Integrations", icon: Plug, available: true },
  { key: "settings", label: "Settings", icon: Settings, available: true },
] as const;

type ModuleKey = (typeof MODULES)[number]["key"];

function AdminDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState<ModuleKey>(() => {
    if (typeof window === "undefined") return "overview";
    const m = new URLSearchParams(window.location.search).get("module") as ModuleKey | null;
    return m && MODULES.some((x) => x.key === m) ? m : "overview";
  });
  const checkAdmin = useServerFn(checkIsAdmin);

  const adminQuery = useQuery({
    queryKey: ["check-admin"],
    queryFn: () => checkAdmin(),
  });

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  if (adminQuery.isLoading) {
    return (
      <Container className="py-16">
        <p className="text-[var(--muted-foreground)]">Loading…</p>
      </Container>
    );
  }

  if (!adminQuery.data?.isAdmin) {
    return (
      <Container className="max-w-xl py-16">
        <div className="rounded-xl border border-border bg-white p-8">
          <h1 className="text-2xl font-semibold text-primary">Admin access required</h1>
          <p className="mt-3 text-sm text-[var(--muted-foreground)]">
            Your account is signed in but does not have the <code>admin</code> role yet. Ask the
            site owner to grant it.
          </p>
          <p className="mt-2 text-xs text-[var(--muted-foreground)]">
            User ID: <code>{adminQuery.data?.userId}</code>
          </p>
          <button
            onClick={signOut}
            className="mt-6 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-[var(--surface)]"
          >
            Sign out
          </button>
        </div>
      </Container>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <header className="border-b border-border bg-white">
        <Container className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-[var(--muted-foreground)] hover:text-primary">
              ← Site
            </Link>
            <h1 className="text-lg font-semibold text-primary">Admin Dashboard</h1>
          </div>
          <button
            onClick={signOut}
            className="text-sm text-[var(--muted-foreground)] hover:text-primary"
          >
            Sign out
          </button>
        </Container>
      </header>

      <Container className="grid grid-cols-1 gap-6 py-8 lg:grid-cols-[220px_1fr]">
        <aside>
          <nav className="space-y-1">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const isActive = m.key === active;
              return (
                <button
                  key={m.key}
                  type="button"
                  disabled={!m.available}
                  onClick={() => m.available && setActive(m.key)}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition ${
                    isActive
                      ? "bg-primary text-white"
                      : m.available
                        ? "text-foreground hover:bg-white"
                        : "cursor-not-allowed text-[var(--muted-foreground)] opacity-60"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{m.label}</span>
                  {!m.available && (
                    <span className="ml-auto text-[10px] uppercase tracking-wide">Soon</span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="min-w-0">
          {active === "overview" && <AdminOverview onNavigate={setActive} />}
          {active === "settings" && <SettingsModule />}
          {active === "pages" && <PagesModule />}
          {active === "leads" && <LeadsModule />}
          {active === "chatQuestions" && <ChatQuestionsModule />}
          {active === "calculator" && <CalculatorModule />}
          {active === "bookings" && <BookingsModule />}
          {active === "analytics" && <AnalyticsModule />}
          {active === "pageImages" && <PageImagesModule />}
          {active === "autoImages" && <AutoImagesModule />}
          {active === "media" && <MediaLibraryModule />}
          {active === "blog" && <BlogModule />}
          {active === "interviews" && (
            <InterviewPostsModule
              onOpenReview={(id) =>
                navigate({ to: "/interview-review/$sessionId", params: { sessionId: id } })
              }
            />
          )}
          {active === "generator" && <ContentGeneratorModule />}
          {active === "gbp" && <DraftsModule type="gbp" />}
          {active === "social" && <DraftsModule type="social" />}
          {active === "stories" && <DraftsModule type="stories" />}
          {active === "drive" && <DriveSyncModule />}
          {active === "integrations" && <IntegrationsModule />}
        </main>
      </Container>
    </div>
  );
}

/* ========== Settings ========== */
function AdminOverview({ onNavigate }: { onNavigate: (key: ModuleKey) => void }) {
  const getStats = useServerFn(getAdminStats);
  const getSettings = useServerFn(getSiteSettings);
  const getNeeds = useServerFn(getPagesNeedingPhotos);
  const statsQuery = useQuery({ queryKey: ["admin-stats"], queryFn: () => getStats() });
  const settingsQuery = useQuery({ queryKey: ["site-settings"], queryFn: () => getSettings() });
  const needsQuery = useQuery({ queryKey: ["pages-needing-photos"], queryFn: () => getNeeds() });

  const cards = [
    { label: "Total leads", value: statsQuery.data?.leadsTotal, accent: "text-primary" },
    { label: "Uncontacted leads", value: statsQuery.data?.leadsUncontacted, accent: "text-accent" },
    { label: "Leads (last 7 days)", value: statsQuery.data?.leadsLast7Days, accent: "text-primary" },
    { label: "Published posts", value: statsQuery.data?.blogPublished, accent: "text-primary" },
    { label: "Blog drafts", value: statsQuery.data?.blogDrafts, accent: "text-primary" },
    { label: "Interview drafts pending", value: statsQuery.data?.interviewDraftsPending, accent: "text-accent" },
    { label: "Images missing alt text", value: statsQuery.data?.mediaMissingAlt, accent: "text-accent" },
  ];

  const ga4Connected = Boolean(settingsQuery.data?.ga4MeasurementId);
  const gscConnected = Boolean(settingsQuery.data?.gscVerificationToken);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary">Dashboard</h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Live snapshot of leads, content, and integrations.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-border bg-white p-5">
            <p className="text-xs uppercase tracking-wide text-[var(--muted-foreground)]">
              {c.label}
            </p>
            <p className={`mt-2 text-3xl font-semibold ${c.accent}`}>
              {statsQuery.isLoading ? "…" : (c.value ?? 0)}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <IntegrationStatusCard
          title="Google Analytics 4"
          description="Track pageviews, lead form conversions, and audiences."
          connected={ga4Connected}
          connectedAt={settingsQuery.data?.ga4ConnectedAt ?? null}
          identifier={settingsQuery.data?.ga4MeasurementId ?? ""}
          onConnect={() => onNavigate("settings")}
        />
        <IntegrationStatusCard
          title="Google Search Console"
          description="Verify ownership and monitor search performance."
          connected={gscConnected}
          connectedAt={settingsQuery.data?.gscConnectedAt ?? null}
          identifier={settingsQuery.data?.gscVerificationToken ?? ""}
          onConnect={() => onNavigate("settings")}
        />
      </div>

      <div className="rounded-xl border border-border bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-primary">Pages needing real photos</h3>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Pages with empty image slots, or slots flagged as placeholder.
            </p>
          </div>
          <button
            onClick={() => onNavigate("drive")}
            className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
          >
            Open Drive Sync
          </button>
        </div>
        <div className="mt-4">
          {needsQuery.isLoading ? (
            <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
          ) : (needsQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-green-700">All pages have real photos. Nice work.</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {(needsQuery.data ?? []).slice(0, 10).map((p) => (
                <li key={p.pageSlug} className="flex items-center justify-between gap-3 py-2">
                  <span className="truncate font-medium">/{p.pageSlug}</span>
                  <span className="flex items-center gap-2 text-xs">
                    {p.unassignedSlots > 0 && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                        {p.unassignedSlots} empty
                      </span>
                    )}
                    {p.placeholderSlots > 0 && (
                      <span className="rounded bg-orange-100 px-1.5 py-0.5 text-orange-800">
                        {p.placeholderSlots} placeholder
                      </span>
                    )}
                    <button
                      onClick={() => onNavigate("pageImages")}
                      className="rounded border border-border px-2 py-0.5 text-[11px] hover:bg-[var(--surface)]"
                    >
                      Fix
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-5">
        <h3 className="text-sm font-semibold text-primary">Quick actions</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => onNavigate("leads")}
            className="rounded-md border border-border bg-[var(--surface)] px-3 py-2 text-sm font-medium hover:border-accent"
          >
            View leads
          </button>
          <button
            onClick={() => onNavigate("drive")}
            className="rounded-md border border-border bg-[var(--surface)] px-3 py-2 text-sm font-medium hover:border-accent"
          >
            Sync Google Drive
          </button>
          <button
            onClick={() => onNavigate("blog")}
            className="rounded-md border border-border bg-[var(--surface)] px-3 py-2 text-sm font-medium hover:border-accent"
          >
            Write a blog post
          </button>
          <button
            onClick={() => onNavigate("settings")}
            className="rounded-md border border-border bg-[var(--surface)] px-3 py-2 text-sm font-medium hover:border-accent"
          >
            Edit site settings
          </button>
        </div>
      </div>
    </div>
  );
}

function IntegrationStatusCard({
  title,
  description,
  connected,
  connectedAt,
  identifier,
  onConnect,
}: {
  title: string;
  description: string;
  connected: boolean;
  connectedAt: string | null;
  identifier: string;
  onConnect: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-primary">{title}</h3>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">{description}</p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
            connected ? "bg-green-100 text-green-800" : "bg-[var(--surface)] text-[var(--muted-foreground)]"
          }`}
        >
          {connected ? "Connected" : "Not connected"}
        </span>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="truncate text-xs text-[var(--muted-foreground)]">
          {connected
            ? `${identifier}${connectedAt ? ` · since ${new Date(connectedAt).toLocaleDateString()}` : ""}`
            : "Add this after launching with the live domain."}
        </p>
        <button
          onClick={onConnect}
          className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
        >
          {connected ? "Manage" : "Connect"}
        </button>
      </div>
    </div>
  );
}

/* ========== Settings ========== */
type SettingsForm = {
  rating: string;
  reviewCount: string;
  businessName: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  textPhone: string;
  emailContact: string;
  facebookUrl: string;
  instagramUrl: string;
  youtubeUrl: string;
  linkedinUrl: string;
  ga4MeasurementId: string;
};


function SettingsModule() {
  const qc = useQueryClient();
  const getSettings = useServerFn(getSiteSettings);
  const updateSettings = useServerFn(updateSiteSettings);
  const connectGsc = useServerFn(connectGoogleSearchConsole);
  const disconnectGsc = useServerFn(disconnectGoogleSearchConsole);

  const settingsQuery = useQuery({

    queryKey: ["site-settings"],
    queryFn: () => getSettings(),
  });

  const [form, setForm] = useState<SettingsForm | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    const d = settingsQuery.data;
    if (d && !form) {
      setForm({
        rating: String(d.rating),
        reviewCount: String(d.reviewCount),
        businessName: d.businessName,
        street: d.street,
        city: d.city,
        state: d.state,
        postalCode: d.postalCode,
        phone: d.phone,
        textPhone: d.textPhone,
        emailContact: d.emailContact,
        facebookUrl: d.facebookUrl,
        instagramUrl: d.instagramUrl,
        youtubeUrl: d.youtubeUrl,
        linkedinUrl: d.linkedinUrl,
        ga4MeasurementId: d.ga4MeasurementId,
      });

    }
  }, [settingsQuery.data, form]);

  const mutation = useMutation({
    mutationFn: (vars: Record<string, unknown>) =>
      updateSettings({ data: vars as never }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site-settings"] });
      setSavedMsg("Saved. Changes are live across the site.");
      setTimeout(() => setSavedMsg(null), 4000);
    },
  });

  function update<K extends keyof SettingsForm>(key: K, value: string) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    const r = Number(form.rating);
    const c = Number(form.reviewCount);
    if (Number.isNaN(r) || Number.isNaN(c)) return;
    mutation.mutate({
      rating: r,
      reviewCount: c,
      businessName: form.businessName,
      street: form.street,
      city: form.city,
      state: form.state,
      postalCode: form.postalCode,
      phone: form.phone,
      textPhone: form.textPhone,
      emailContact: form.emailContact,
      facebookUrl: form.facebookUrl,
      instagramUrl: form.instagramUrl,
      youtubeUrl: form.youtubeUrl,
      linkedinUrl: form.linkedinUrl,
      ga4MeasurementId: form.ga4MeasurementId,
    });

  }

  if (settingsQuery.isLoading || !form) {
    return (
      <div className="rounded-xl border border-border bg-white p-6">
        <p className="text-sm text-[var(--muted-foreground)]">Loading settings…</p>
      </div>
    );
  }

  const ga4Connected = Boolean(settingsQuery.data?.ga4MeasurementId);

  return (

    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary">Settings</h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Business info, social links, and analytics. Changes are live immediately.
        </p>
      </div>

      {/* Business info */}
      <section className="rounded-xl border border-border bg-white p-6">
        <h3 className="text-lg font-semibold text-primary">Business info</h3>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Used in the site footer, contact section, and structured-data schema.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Business name" value={form.businessName} onChange={(v) => update("businessName", v)} />
          <Field label="Email" value={form.emailContact} onChange={(v) => update("emailContact", v)} />
          <Field label="Street" value={form.street} onChange={(v) => update("street", v)} />
          <div className="grid grid-cols-3 gap-2">
            <Field label="City" value={form.city} onChange={(v) => update("city", v)} />
            <Field label="State" value={form.state} onChange={(v) => update("state", v)} />
            <Field label="ZIP" value={form.postalCode} onChange={(v) => update("postalCode", v)} />
          </div>
          <Field label="Phone (call)" value={form.phone} onChange={(v) => update("phone", v)} />
          <Field label="Phone (text)" value={form.textPhone} onChange={(v) => update("textPhone", v)} />
          <Field
            label="Google rating (0–5)"
            type="number"
            step="0.1"
            min="0"
            max="5"
            value={form.rating}
            onChange={(v) => update("rating", v)}
          />
          <Field
            label="Google review count"
            type="number"
            min="0"
            value={form.reviewCount}
            onChange={(v) => update("reviewCount", v)}
          />
        </div>
      </section>

      {/* Social */}
      <section className="rounded-xl border border-border bg-white p-6">
        <h3 className="text-lg font-semibold text-primary">Social links</h3>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Shown in the site header strip and footer. Leave blank to hide.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Facebook URL" value={form.facebookUrl} onChange={(v) => update("facebookUrl", v)} />
          <Field label="Instagram URL" value={form.instagramUrl} onChange={(v) => update("instagramUrl", v)} />
          <Field label="YouTube URL" value={form.youtubeUrl} onChange={(v) => update("youtubeUrl", v)} />
          <Field label="LinkedIn URL" value={form.linkedinUrl} onChange={(v) => update("linkedinUrl", v)} />
        </div>
      </section>

      {/* Analytics */}
      <section className="rounded-xl border border-border bg-white p-6">
        <h3 className="text-lg font-semibold text-primary">Analytics & search</h3>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Connect after the site is live at the production domain.
        </p>
        <div className="mt-5 space-y-4">
          <ConnectField
            label="GA4 Measurement ID"
            placeholder="G-XXXXXXXXXX"
            value={form.ga4MeasurementId}
            connected={ga4Connected}
            connectedAt={settingsQuery.data?.ga4ConnectedAt ?? null}
            onChange={(v) => update("ga4MeasurementId", v)}
          />
          <GscConnectCard
            connected={Boolean(settingsQuery.data?.gscVerificationToken)}
            connectedAt={settingsQuery.data?.gscConnectedAt ?? null}
            onSuccess={() => qc.invalidateQueries({ queryKey: ["site-settings"] })}
          />

        </div>
      </section>

      {mutation.isError && (
        <p className="text-sm text-red-600">
          {mutation.error instanceof Error ? mutation.error.message : "Update failed"}
        </p>
      )}
      {savedMsg && <p className="text-sm text-green-700">{savedMsg}</p>}

      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--muted-foreground)]">
          {settingsQuery.data?.updatedAt
            ? `Last updated ${new Date(settingsQuery.data.updatedAt).toLocaleString()}`
            : "Not yet updated"}
        </p>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          {mutation.isPending ? "Saving…" : "Save all changes"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  min?: string;
  max?: string;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm focus:border-accent focus:outline-none"
        {...rest}
      />
    </label>
  );
}

function ConnectField({
  label,
  value,
  placeholder,
  connected,
  connectedAt,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  connected: boolean;
  connectedAt: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-[var(--surface)] p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium">{label}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
            connected ? "bg-green-100 text-green-800" : "bg-white text-[var(--muted-foreground)] border border-border"
          }`}
        >
          {connected ? `Connected${connectedAt ? ` · ${new Date(connectedAt).toLocaleDateString()}` : ""}` : "Not connected"}
        </span>
      </div>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-3 w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:border-accent focus:outline-none"
      />
      <p className="mt-2 text-xs text-[var(--muted-foreground)]">
        Save to {connected ? "update" : "connect"}. Clear the field and save to disconnect.
      </p>
    </div>
  );
}

function GscConnectCard({
  connected,
  connectedAt,
  onSuccess,
}: {
  connected: boolean;
  connectedAt: string | null;
  onSuccess: () => void;
}) {
  const connect = useServerFn(connectGoogleSearchConsole);
  const disconnect = useServerFn(disconnectGoogleSearchConsole);
  const [status, setStatus] = useState<{ type: "idle" | "loading" | "error" | "success"; message?: string }>({
    type: "idle",
  });

  async function handleConnect() {
    setStatus({ type: "loading" });
    const res = await connect({ data: undefined });
    if (res.ok) {
      setStatus({
        type: "success",
        message: `Google Search Console connected for ${res.domain}.`,
      });
      onSuccess();
    } else {
      setStatus({ type: "error", message: res.error });
    }
  }

  async function handleDisconnect() {
    setStatus({ type: "loading" });
    const res = await disconnect({ data: undefined });
    if (res.ok) {
      setStatus({ type: "success", message: "Google Search Console disconnected." });
      onSuccess();
    } else {
      setStatus({ type: "error", message: res.error ?? "Disconnect failed" });
    }
  }

  return (
    <div className="rounded-lg border border-border bg-[var(--surface)] p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium">Google Search Console</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
            connected
              ? "bg-green-100 text-green-800"
              : "bg-white text-[var(--muted-foreground)] border border-border"
          }`}
        >
          {connected
            ? `Connected${connectedAt ? ` · ${new Date(connectedAt).toLocaleDateString()}` : ""}`
            : "Not connected"}
        </span>
      </div>

      <p className="mt-3 text-sm text-[var(--muted-foreground)]">
        Verifies ownership for <strong>https://www.durafoaminc.com</strong> and adds it to your
        Search Console account.
      </p>

      {status.type !== "idle" && status.message && (
        <div
          className={`mt-3 rounded-md px-3 py-2 text-sm ${
            status.type === "error"
              ? "bg-red-50 text-red-700"
              : status.type === "success"
                ? "bg-green-50 text-green-700"
                : "bg-white text-[var(--muted-foreground)]"
          }`}
        >
          {status.type === "loading" ? "Working…" : status.message}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        {!connected ? (
          <button
            type="button"
            onClick={handleConnect}
            disabled={status.type === "loading"}
            className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            <Plug className="h-4 w-4" /> Connect Google Search Console
          </button>
        ) : (
          <>
            <span className="text-sm text-[var(--muted-foreground)]">
              The verification meta tag is live on the site.
            </span>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={status.type === "loading"}
              className="ml-auto flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" /> Disconnect
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ========== Media Library ========== */


// Tag → short phrase used to build readable titles and alt text.
const TAG_PHRASE: Record<string, string> = {
  Foam: "foam roof",
  Tile: "tile roof",
  Shingle: "shingle roof",
  Recoat: "foam roof recoat",
  Repair: "roof repair",
  Leak: "roof leak repair",
  Skylights: "skylight",
  Commercial: "commercial roof",
  Team: "Durafoam Roofing team",
  Logos: "Durafoam Roofing logo",
  "Before/After": "before & after",
  Video: "roofing project video",
  Cities: "Phoenix-area roofing",
  Blog: "blog",
};

function filenameBase(name: string): string {
  return name.replace(/\.[^.]+$/, "");
}

function buildSuggestedTitle(tags: string[]): string {
  if (tags.length === 0) return "";
  const phrases = tags.map((t) => TAG_PHRASE[t] ?? t.toLowerCase());
  const joined = phrases.join(" • ");
  const cap = joined.charAt(0).toUpperCase() + joined.slice(1);
  return `${cap} — Durafoam Roofing Phoenix`;
}

function buildSuggestedAlt(tags: string[]): string {
  if (tags.length === 0) return "";
  const phrases = tags.map((t) => TAG_PHRASE[t] ?? t.toLowerCase());
  return `${phrases.join(", ")} job in Phoenix, AZ by Durafoam Roofing`;
}

/** Returns true if the current title looks like the raw uploaded filename. */
function looksLikeRawFilename(title: string, filename: string): boolean {
  if (!title) return true;
  const base = filenameBase(filename).trim();
  if (title.trim() === base) return true;
  // common camera/raw patterns: IMG_1234, DSC01234, PXL_..., etc.
  return /^(IMG|DSC|DSCN|PXL|MVI|GOPR|P\d|VID)[_-]?\d+/i.test(title.trim());
}

async function fileToWebp(file: File): Promise<{ blob: Blob; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(bitmap, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", 0.88),
  );
  if (!blob) throw new Error("WebP conversion failed");
  return { blob, width: bitmap.width, height: bitmap.height };
}

function MediaLibraryModule() {
  const qc = useQueryClient();
  const listFn = useServerFn(listMediaAssets);
  const createFn = useServerFn(createMediaAsset);
  const updateFn = useServerFn(updateMediaAsset);
  const deleteFn = useServerFn(deleteMediaAsset);
  const recategorizeFn = useServerFn(bulkCategorizeImages);

  const query = useQuery({ queryKey: ["media-assets"], queryFn: () => listFn() });
  const usageFn = useServerFn(listMediaUsage);
  const usageQuery = useQuery({ queryKey: ["media-usage"], queryFn: () => usageFn() });
  const usageMap = useMemo(() => {
    const m = new Map<string, { ref: string; type: "page" | "blog" }[]>();
    for (const u of usageQuery.data ?? []) m.set(u.mediaAssetId, u.refs);
    return m;
  }, [usageQuery.data]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("");
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingTags, setPendingTags] = useState<Set<string>>(new Set());
  const [replaceMode, setReplaceMode] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  // Per-card pending edits, pushed up from MediaCard via onChange.
  const editsRef = useRef<Map<string, { title: string; altText: string; categories: string[]; dirty: boolean }>>(new Map());

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function togglePendingTag(c: string) {
    setPendingTags((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }
  function clearSelection() {
    setSelected(new Set());
    setPendingTags(new Set());
    setReplaceMode(false);
  }

  const createMutation = useMutation({
    mutationFn: (input: {
      url: string;
      storagePath: string;
      filename: string;
      type: string;
      width: number;
      height: number;
      fileSize: number;
      altText: string;
      title: string;
      categories: string[];
      webpUrl: string;
    }) => createFn({ data: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media-assets"] }),
  });
  const updateMutation = useMutation({
    mutationFn: (input: { id: string; altText?: string; title?: string; categories?: string[] }) =>
      updateFn({ data: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media-assets"] }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media-assets"] }),
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of Array.from(files)) {
        const { blob, width, height } = await fileToWebp(file);
        const baseName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();
        const path = `${Date.now()}-${baseName}.webp`;
        const { error: upErr } = await supabase.storage
          .from("media")
          .upload(path, blob, { contentType: "image/webp", upsert: false });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("media").getPublicUrl(path);
        await createMutation.mutateAsync({
          url: pub.publicUrl,
          storagePath: path,
          filename: file.name,
          type: "image/webp",
          width,
          height,
          fileSize: blob.size,
          altText: "",
          title: file.name.replace(/\.[^.]+$/, ""),
          categories: [],
          webpUrl: pub.publicUrl,
        });
      }
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  const isVideoAsset = (m: { type?: string | null }) =>
    (m.type ?? "").startsWith("video");

  const filtered = useMemo(() => {
    if (!query.data) return [];
    let rows = query.data;
    if (filter === "") {
      // Untagged = images only (videos don't need tags)
      rows = rows.filter((m) => !isVideoAsset(m) && (!m.categories || m.categories.length === 0));
    } else if (filter === "__tagged__") {
      rows = rows.filter((m) => !isVideoAsset(m) && m.categories && m.categories.length > 0);
    } else if (filter === "__video__") {
      rows = rows.filter((m) => isVideoAsset(m));
    } else {
      rows = rows.filter((m) =>
        m.categories.some((c) => c.toLowerCase() === filter.toLowerCase()),
      );
    }
    if (unusedOnly) rows = rows.filter((m) => (usageMap.get(m.id) ?? []).length === 0);
    return rows;
  }, [query.data, filter, unusedOnly, usageMap]);




  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of query.data ?? []) {
      for (const c of m.categories ?? []) {
        const key = c.toLowerCase();
        map.set(key, (map.get(key) ?? 0) + 1);
      }
    }
    return map;
  }, [query.data]);
  const totalCount = query.data?.length ?? 0;
  const videoCount = useMemo(
    () => (query.data ?? []).filter(isVideoAsset).length,
    [query.data],
  );
  const untaggedCount = useMemo(
    () => (query.data ?? []).filter((m) => !isVideoAsset(m) && (!m.categories || m.categories.length === 0)).length,
    [query.data],
  );
  const taggedCount = totalCount - untaggedCount - videoCount;

  const allTags = useMemo(() => {
    const set = new Map<string, string>(); // lowercase -> display
    for (const t of MEDIA_CATEGORIES) set.set(t.toLowerCase(), t);
    for (const m of query.data ?? []) {
      for (const c of m.categories ?? []) {
        const key = c.toLowerCase();
        if (!set.has(key)) set.set(key, c);
      }
    }
    return Array.from(set.values());
  }, [query.data]);




  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-primary">Media Library</h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Upload JPG/PNG/WebP. Files are converted to WebP and served from the media bucket.
          </p>
        </div>
        <div>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
          >
            <Upload className="h-4 w-4" />
            {uploading ? "Uploading…" : "Upload images"}
          </button>
        </div>
      </div>

      {uploadError && (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {uploadError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilter("")}
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            filter === "" ? "bg-primary text-white" : "bg-white text-foreground border border-border"
          }`}
        >
          Untagged ({untaggedCount})
        </button>
        <button
          type="button"
          onClick={() => setFilter("__tagged__")}
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            filter === "__tagged__" ? "bg-primary text-white" : "bg-white text-foreground border border-border"
          }`}
        >
          All tagged ({taggedCount})
        </button>
        <button
          type="button"
          onClick={() => setFilter("__video__")}
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            filter === "__video__" ? "bg-primary text-white" : "bg-white text-foreground border border-border"
          }`}
        >
          Video ({videoCount})
        </button>
        <span className="text-xs text-[var(--muted-foreground)]">Total: {totalCount}</span>

        {allTags.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setFilter(c)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              filter === c
                ? "bg-primary text-white"
                : "bg-white text-foreground border border-border"
            }`}
          >
            {c} ({categoryCounts.get(c.toLowerCase()) ?? 0})
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={unusedOnly}
            onChange={(e) => setUnusedOnly(e.target.checked)}
          />
          Unused only
        </label>
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 z-50 w-[min(96vw,960px)] -translate-x-1/2 rounded-xl border border-accent bg-white p-3 shadow-lg">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-primary">{selected.size} selected</span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-[var(--muted-foreground)] hover:underline"
            >
              Clear
            </button>
            <div className="mx-1 h-5 w-px bg-border" />
            <button
              type="button"
              disabled={bulkBusy}
              onClick={async () => {
                setBulkBusy(true);
                try {
                  let saved = 0;
                  for (const id of Array.from(selected)) {
                    const e = editsRef.current.get(id);
                    if (!e || !e.dirty) continue;
                    await updateMutation.mutateAsync({
                      id,
                      title: e.title,
                      altText: e.altText,
                      categories: e.categories,
                    });
                    saved++;
                  }
                  if (saved === 0) {
                    alert("No unsaved edits on the selected cards.");
                  } else {
                    clearSelection();
                  }
                } finally {
                  setBulkBusy(false);
                }
              }}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {bulkBusy ? "Saving…" : "Save edits on selected cards"}
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={async () => {
                if (!confirm(`Delete ${selected.size} image(s)? Any pages using them revert to the placeholder.`)) return;
                setBulkBusy(true);
                try {
                  for (const id of Array.from(selected)) {
                    await deleteMutation.mutateAsync(id);
                  }
                  clearSelection();
                } finally {
                  setBulkBusy(false);
                }
              }}
              className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete selected
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={async () => {
                if (
                  !confirm(
                    `Re-categorize ${selected.size} image(s) with AI?\n\nThis REPLACES the service/role/city tags with fresh AI picks (≤3 services, ≤1 role, 1 city from filename). Custom tags outside the controlled vocabulary are preserved.`,
                  )
                )
                  return;
                setBulkBusy(true);
                try {
                  const ids = Array.from(selected);
                  const r = await recategorizeFn({ data: { mode: "selected", ids, limit: 500 } });
                  qc.invalidateQueries({ queryKey: ["media-assets"] });
                  clearSelection();
                  alert(`Re-categorized ${r.tagged} of ${r.total}. ${r.failed} failed.`);
                } catch (e) {
                  alert(`Re-categorize failed: ${e instanceof Error ? e.message : String(e)}`);
                } finally {
                  setBulkBusy(false);
                }
              }}
              className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
              title="Replace tags using AI with stricter rules (≤3 services, ≤1 role, 1 city)"
            >
              <Sparkles className="h-3.5 w-3.5" /> Re-categorize with AI
            </button>
          </div>

          <div className="mt-3 border-t border-border pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-foreground">Bulk tags:</span>
              <label className="flex items-center gap-1 text-[11px] text-[var(--muted-foreground)]">
                <input
                  type="checkbox"
                  checked={replaceMode}
                  onChange={(e) => setReplaceMode(e.target.checked)}
                />
                Replace existing tags
              </label>
              <span className="text-[11px] text-[var(--muted-foreground)]">
                {replaceMode
                  ? "These tags will overwrite the tags on every selected image."
                  : "These tags will be added to every selected image, on top of its own tags."}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <TagPicker
                all={allTags}
                selected={Array.from(pendingTags)}
                onAdd={(t) => togglePendingTag(t)}
                onRemove={(t) => togglePendingTag(t)}
              />

              <button
                type="button"
                disabled={bulkBusy || pendingTags.size === 0}
                onClick={async () => {
                  setBulkBusy(true);
                  try {
                    const tags = Array.from(pendingTags);
                    const byId = new Map((query.data ?? []).map((m) => [m.id, m]));
                    for (const id of Array.from(selected)) {
                      const m = byId.get(id);
                      if (!m) continue;
                      const next = replaceMode
                        ? tags
                        : Array.from(new Set([...(m.categories ?? []), ...tags]));
                      await updateMutation.mutateAsync({ id, categories: next });
                    }
                    clearSelection();
                  } finally {
                    setBulkBusy(false);
                  }
                }}
                className="ml-auto rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {bulkBusy ? "Applying…" : `${replaceMode ? "Replace" : "Add"} on ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {query.isLoading ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center">
          <ImageIcon className="mx-auto h-8 w-8 text-[var(--muted-foreground)]" />
          <p className="mt-3 text-sm text-[var(--muted-foreground)]">
            No images yet. Upload one above or sync your Google Drive.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((m) => (
            <MediaCard
              key={m.id}
              asset={m}
              allTags={allTags}
              usage={usageMap.get(m.id) ?? []}
              selected={selected.has(m.id)}
              onToggleSelected={() => toggleSelected(m.id)}
              onPreview={() => setPreviewId(m.id)}
              onChange={(edits) => {
                editsRef.current.set(m.id, edits);
              }}
              onSave={(patch) => {
                updateMutation.mutate({ id: m.id, ...patch });
                editsRef.current.delete(m.id);
              }}
              onDelete={() => {
                if (confirm("Delete this image? Any page assignments using it will revert to the placeholder.")) {
                  deleteMutation.mutate(m.id);
                  editsRef.current.delete(m.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {previewId && (() => {
        const asset = (query.data ?? []).find((m) => m.id === previewId);
        if (!asset) return null;
        return (
          <ImageLightbox
            url={asset.url}
            alt={asset.altText || asset.filename}
            onClose={() => setPreviewId(null)}
            onCropSave={async (blob, width, height, aspectLabel) => {
              const baseName = asset.filename
                .replace(/\.[^.]+$/, "")
                .replace(/[^a-z0-9-_]+/gi, "-")
                .toLowerCase();
              const ratioSlug = aspectLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              const path = `${Date.now()}-${baseName}-${ratioSlug}.webp`;
              const { error: upErr } = await supabase.storage
                .from("media")
                .upload(path, blob, { contentType: "image/webp", upsert: false });
              if (upErr) throw upErr;
              const { data: pub } = supabase.storage.from("media").getPublicUrl(path);
              await createMutation.mutateAsync({
                url: pub.publicUrl,
                storagePath: path,
                filename: `${baseName}-${ratioSlug}.webp`,
                type: "image/webp",
                width,
                height,
                fileSize: blob.size,
                altText: asset.altText,
                title: `${asset.title} (${aspectLabel})`,
                categories: asset.categories ?? [],
                webpUrl: pub.publicUrl,
              });
            }}
          />
        );
      })()}
    </div>
  );
}

function MediaCard({
  asset,
  allTags,
  usage,
  selected,
  onToggleSelected,
  onPreview,
  onChange,
  onSave,
  onDelete,
}: {
  asset: MediaAsset;
  allTags: string[];
  usage: { ref: string; type: "page" | "blog" }[];
  selected: boolean;
  onToggleSelected: () => void;
  onPreview: () => void;
  onChange?: (edits: { title: string; altText: string; categories: string[]; dirty: boolean }) => void;
  onSave: (patch: { altText?: string; title?: string; categories?: string[] }) => void;
  onDelete: () => void;
}) {
  const [alt, setAlt] = useState(asset.altText);
  const [title, setTitle] = useState(asset.title);
  const [cats, setCats] = useState<string[]>(asset.categories);
  const dirty = alt !== asset.altText || title !== asset.title || cats.join("|") !== asset.categories.join("|");
  const isVideo = (asset.type ?? "").startsWith("video");

  // Push edits up so the bulk "Save edits on selected cards" can collect them.
  useEffect(() => {
    onChange?.({ title, altText: alt, categories: cats, dirty });
  }, [title, alt, cats, dirty, onChange]);

  function toggleCat(c: string) {
    const adding = !cats.includes(c);
    const next = adding ? [...cats, c] : cats.filter((x) => x !== c);
    setCats(next);
    // Auto-suggest title + alt from the new tag set.
    const suggestedTitle = buildSuggestedTitle(next);
    const suggestedAlt = buildSuggestedAlt(next);
    if (suggestedTitle && (looksLikeRawFilename(title, asset.filename) || title.trim() === "")) {
      setTitle(suggestedTitle);
    }
    if (suggestedAlt && alt.trim() === "") {
      setAlt(suggestedAlt);
    }
    // Auto-select the card the first time a tag is added, so bulk "Save edits"
    // picks it up without an extra click on the checkbox.
    if (adding && !selected) {
      onToggleSelected();
    }
  }

  const rawRatio = asset.width && asset.height ? asset.width / asset.height : 4 / 3;
  const ratio = Math.min(2.2, Math.max(0.5, rawRatio));

  return (
    <div className={`overflow-hidden rounded-xl border bg-white ${selected ? "border-accent ring-2 ring-accent" : "border-border"}`}>
      <div
        className="relative w-full bg-[var(--surface)]"
        style={{ aspectRatio: String(ratio) }}
      >
        <label className="absolute left-2 top-2 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded bg-white/90 shadow">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            className="h-4 w-4 cursor-pointer accent-accent"
            aria-label="Select image"
          />
        </label>
        {isVideo ? (
          <video
            src={asset.url}
            className="h-full w-full object-contain"
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <button
            type="button"
            onClick={onPreview}
            className="block h-full w-full cursor-zoom-in"
            aria-label="Preview image"
          >
            <img
              src={asset.url}
              alt={asset.altText || asset.filename}
              width={asset.width ?? 800}
              height={asset.height ?? 600}
              loading="lazy"
              className="h-full w-full object-contain"
            />
          </button>
        )}
        {usage.length > 0 ? (
          <span className="absolute bottom-2 left-2 rounded bg-green-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            In use · {usage.length}
          </span>
        ) : (
          <span className="absolute bottom-2 left-2 rounded bg-zinc-500/80 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            Unused
          </span>
        )}
        {isVideo && (
          <span className="absolute right-2 top-2 rounded bg-purple-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            Video
          </span>
        )}
      </div>
      <div className="space-y-2 p-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="w-full rounded border border-border px-2 py-1 text-xs"
        />
        <textarea
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder="Alt text"
          rows={2}
          className="w-full rounded border border-border px-2 py-1 text-xs"
        />
        {!isVideo && (
          <div className="flex flex-wrap gap-1">
            <TagPicker
              all={allTags}
              selected={cats}
              onAdd={(t) => toggleCat(t)}
              onRemove={(t) => toggleCat(t)}
              chipSize="xs"
            />
          </div>
        )}

        {usage.length > 0 && (

          <div className="flex flex-wrap gap-1 border-t border-border pt-2 text-[10px]">
            <span className="text-[var(--muted-foreground)]">On:</span>
            {usage.slice(0, 6).map((u) => (
              <a
                key={`${u.type}-${u.ref}`}
                href={u.type === "blog" ? `/blog/${u.ref}` : `/${u.ref === "home" ? "" : u.ref}`}
                target="_blank"
                rel="noreferrer"
                className="rounded bg-[var(--surface)] px-1.5 py-0.5 hover:underline"
              >
                /{u.ref}
              </a>
            ))}
            {usage.length > 6 && <span>+{usage.length - 6}</span>}
          </div>
        )}
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1.5 text-red-600 hover:bg-red-50"
            aria-label="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={!dirty}
            onClick={() => onSave({ altText: alt, title, categories: cats })}
            className="rounded bg-accent px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========== Page Images ========== */
function PageImagesModule() {
  const [selected, setSelected] = useState<string>(PAGES[0].slug);
  const page = PAGES.find((p) => p.slug === selected) ?? PAGES[0];
  const listFn = useServerFn(listAssignmentSlotsByPage);
  const vq = useQuery({
    queryKey: ["assignment-slots-by-page"],
    queryFn: () => listFn(),
    staleTime: 30_000,
  });
  const slotsByPage = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const r of vq.data ?? []) m.set(r.pageSlug, new Set(r.slots));
    return m;
  }, [vq.data]);
  const completeSet = useMemo(() => {
    const s = new Set<string>();
    for (const p of PAGES) {
      const filled = slotsByPage.get(p.slug);
      if (filled && p.slots.every((slot) => filled.has(slot.id))) s.add(p.slug);
    }
    return s;
  }, [slotsByPage]);
  const completeCount = completeSet.size;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary">Page Images</h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Assign images from your Media Library to each slot on each page.
        </p>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        Edits here save to the database and go live immediately on your published site — you don't need to re-publish. The Publish dialog only tracks source code changes, so it will say "up to date" even after image edits.
      </div>


      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="rounded-xl border border-border bg-white p-2">
          <div className="px-2 py-1.5 text-[11px] uppercase tracking-wide text-[var(--muted-foreground)]">
            {completeCount} / {PAGES.length} complete
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {PAGES.map((p) => {
              const isComplete = completeSet.has(p.slug);
              return (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => setSelected(p.slug)}
                  className={`flex w-full items-start gap-2 rounded px-3 py-2 text-left text-sm ${
                    selected === p.slug
                      ? "bg-primary text-white"
                      : "text-foreground hover:bg-[var(--surface)]"
                  }`}
                >
                  <CheckCircle2
                    size={16}
                    className={`mt-0.5 shrink-0 ${
                      isComplete
                        ? selected === p.slug
                          ? "text-white"
                          : "text-emerald-600"
                        : "opacity-20"
                    }`}
                    aria-label={isComplete ? "All images assigned" : "Missing images"}
                  />

                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{p.title}</span>
                    <span className="block text-[11px] opacity-75">{p.path}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="space-y-4">
          <PageHeader page={page} />
          {page.slots.map((slot) => (
            <SlotEditor key={slot.id} page={page} slot={slot} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PageHeader({ page }: { page: PageEntry }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listPageVerifications);
  const setFn = useServerFn(setPageVerified);
  const vq = useQuery({
    queryKey: ["page-verifications"],
    queryFn: () => listFn(),
    staleTime: 30_000,
  });
  const current = vq.data?.find((v) => v.pageSlug === page.slug);
  const isVerified = !!current;
  const mut = useMutation({
    mutationFn: (verified: boolean) =>
      setFn({ data: { pageSlug: page.slug, verified } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["page-verifications"] }),
  });

  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-primary">{page.title}</h3>
          <a
            href={page.path}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-accent hover:underline"
          >
            View page →
          </a>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => mut.mutate(!isVerified)}
            disabled={mut.isPending}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
              isVerified
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "border border-border bg-white text-foreground hover:bg-[var(--surface)]"
            } disabled:opacity-60`}
          >
            {isVerified ? <CheckCircle2 size={14} /> : <Check size={14} />}
            {mut.isPending
              ? "Saving…"
              : isVerified
                ? "Verified · click to unmark"
                : "Mark verified"}
          </button>
          {isVerified && current?.verifiedAt && (
            <span className="text-[11px] text-[var(--muted-foreground)]">
              Verified {new Date(current.verifiedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SlotEditor({ page, slot }: { page: PageEntry; slot: ImageSlot }) {
  const qc = useQueryClient();
  const getFn = useServerFn(getPageAssignments);
  const upsertFn = useServerFn(upsertAssignment);
  const clearFn = useServerFn(clearAssignment);
  const listMediaFn = useServerFn(listMediaAssets);
  const createMediaFn = useServerFn(createMediaAsset);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);


  const aq = useQuery({
    queryKey: ["image-assignments", page.slug],
    queryFn: () => getFn({ data: { pageSlug: page.slug } }),
  });
  const mq = useQuery({ queryKey: ["media-assets"], queryFn: () => listMediaFn() });

  const current = aq.data?.find((a) => a.slot === slot.id);
  const [altText, setAltText] = useState("");
  const [picking, setPicking] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState<number>(60);
  const isHero = slot.id === "hero";
  const allowedOrientations = (slot.orientations ?? (isHero ? ["landscape"] : ["landscape"])) as Array<
    "landscape" | "square" | "portrait"
  >;
  const [orientation, setOrientation] = useState<"landscape" | "square" | "portrait">(
    (current?.orientation ?? slot.defaultOrientation ?? "landscape") as
      | "landscape"
      | "square"
      | "portrait",
  );

  useEffect(() => {
    setAltText(current?.alt ?? slot.suggestedAlt);
  }, [current?.alt, slot.suggestedAlt]);

  useEffect(() => {
    if (current?.orientation) setOrientation(current.orientation);
  }, [current?.orientation]);

  useEffect(() => {
    if (typeof current?.overlayOpacity === "number") {
      setOverlayOpacity(current.overlayOpacity);
    }
  }, [current?.overlayOpacity]);

  const upsert = useMutation({
    mutationFn: (vars: {
      mediaAssetId: string;
      altText: string;
      overlayOpacity?: number;
      orientation?: "landscape" | "square" | "portrait" | null;
    }) =>
      upsertFn({
        data: { pageSlug: page.slug, imageSlot: slot.id, ...vars },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-assignments", page.slug] });
      setPicking(false);
    },
  });
  const clear = useMutation({
    mutationFn: () => clearFn({ data: { pageSlug: page.slug, imageSlot: slot.id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["image-assignments", page.slug] }),
  });
  const regenSeoFn = useServerFn(regenerateAssignmentSeo);
  const regenSeo = useMutation({
    mutationFn: () => regenSeoFn({ data: { pageSlug: page.slug, slot: slot.id } }),
    onSuccess: (r) => {
      setAltText(r.alt);
      qc.invalidateQueries({ queryKey: ["image-assignments", page.slug] });
      if (r.updatedLibrary) qc.invalidateQueries({ queryKey: ["media-assets"] });
    },
  });

  const previewSrc = current?.url ?? placeholderFor(slot);
  const previewAspect =
    isHero
      ? `${slot.width} / ${slot.height}`
      : orientation === "portrait"
        ? "2 / 3"
        : orientation === "square"
          ? "1 / 1"
          : "8 / 5";


  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex items-start gap-4">
        <div
          className="relative w-40 shrink-0 overflow-hidden rounded border border-border bg-[var(--surface)]"
          style={{ aspectRatio: previewAspect }}
        >
          <img
            src={previewSrc}
            alt={altText || slot.suggestedAlt}
            className="h-full w-full object-cover"
          />
          {isHero && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{ backgroundColor: `rgba(15, 26, 66, ${overlayOpacity / 100})` }}
            />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-primary">{slot.label}</p>
              <p className="text-[11px] text-[var(--muted-foreground)]">
                slot: <code>{slot.id}</code> · {slot.width}×{slot.height}
              </p>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                current ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
              }`}
            >
              {current ? "Assigned" : "Placeholder"}
            </span>
          </div>
          <div>
            <label className="block text-xs font-medium">Alt text</label>
            <textarea
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded border border-border px-2 py-1 text-xs"
            />
          </div>
          {!isHero && allowedOrientations.length > 1 && (
            <div>
              <label className="block text-xs font-medium">Orientation</label>
              <div className="mt-1 inline-flex overflow-hidden rounded border border-border">
                {allowedOrientations.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => {
                      setOrientation(o);
                      if (current) {
                        upsert.mutate({
                          mediaAssetId: current.mediaAssetId,
                          altText,
                          orientation: o,
                        });
                      }
                    }}
                    className={`px-2.5 py-1 text-[11px] font-medium capitalize ${
                      orientation === o
                        ? "bg-primary text-white"
                        : "bg-white text-foreground hover:bg-[var(--surface)]"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isHero && (
            <div>
              <label className="flex items-center justify-between text-xs font-medium">
                <span>Darken overlay (for white hero text)</span>
                <span className="text-[var(--muted-foreground)]">{overlayOpacity}%</span>
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={overlayOpacity}
                onChange={(e) => setOverlayOpacity(Number(e.target.value))}
                className="mt-1 w-full"
              />
              {current && overlayOpacity !== (current.overlayOpacity ?? 60) && (
                <button
                  type="button"
                  onClick={() =>
                    upsert.mutate({
                      mediaAssetId: current.mediaAssetId,
                      altText,
                      overlayOpacity,
                    })
                  }
                  disabled={upsert.isPending}
                  className="mt-1 inline-flex items-center gap-1 rounded bg-primary px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                >
                  Save overlay
                </button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="rounded border border-border px-3 py-1.5 text-xs font-medium hover:bg-[var(--surface)]"
            >
              {current ? "Change image" : "Choose image"}
            </button>
            <button
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs font-medium hover:bg-[var(--surface)] disabled:opacity-50"
            >
              <Upload className="h-3 w-3" />
              {uploading ? "Uploading…" : "Upload"}
            </button>
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const files = e.target.files;
                if (!files || files.length === 0) return;
                setUploading(true);
                setUploadError(null);
                try {
                  const created = await uploadFilesToMedia(files, (input) => createMediaFn({ data: input }));
                  await qc.invalidateQueries({ queryKey: ["media-assets"] });
                  const first = created[0];
                  if (first) {
                    upsert.mutate({
                      mediaAssetId: first.id,
                      altText: altText || slot.suggestedAlt,
                      ...(isHero ? { overlayOpacity } : { orientation }),
                    });
                  }
                } catch (err) {
                  setUploadError(err instanceof Error ? err.message : "Upload failed");
                } finally {
                  setUploading(false);
                  if (uploadInputRef.current) uploadInputRef.current.value = "";
                }
              }}
            />
            {current && (() => {
              const altSaved = (current.alt ?? "") === altText && !upsert.isPending;
              return (
              <>
                <button
                  type="button"
                  onClick={() =>
                    upsert.mutate({
                      mediaAssetId: current.mediaAssetId,
                      altText,
                      ...(isHero ? { overlayOpacity } : { orientation }),
                    })
                  }
                  disabled={upsert.isPending || altSaved}
                  className={
                    altSaved
                      ? "inline-flex items-center gap-1 rounded border border-border bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--muted-foreground)] cursor-default"
                      : "inline-flex items-center gap-1 rounded bg-accent px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                  }
                  title={altSaved ? "Alt text is saved and live" : "Save alt text"}
                >
                  <Check className="h-3 w-3" />
                  {upsert.isPending ? "Saving…" : altSaved ? "Saved · live" : "Save alt text"}
                </button>
                <button
                  type="button"
                  onClick={() => regenSeo.mutate()}
                  disabled={regenSeo.isPending}
                  className="inline-flex items-center gap-1 rounded border border-primary px-3 py-1.5 text-xs font-semibold text-primary hover:bg-[var(--surface)] disabled:opacity-50"
                  title="Use AI to rewrite alt text and library title for this image based on page context"
                >
                  <Sparkles className="h-3 w-3" />
                  {regenSeo.isPending ? "Optimizing…" : "Auto-SEO optimize"}
                </button>
                <button
                  type="button"
                  onClick={() => clear.mutate()}
                  disabled={clear.isPending}
                  className="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs hover:bg-red-50 hover:text-red-700"
                >
                  <X className="h-3 w-3" />
                  Reset to placeholder
                </button>
              </>
              );
            })()}
          </div>
          {uploadError && (
            <p className="text-[11px] text-red-600">{uploadError}</p>
          )}
          {regenSeo.isError && (
            <p className="text-[11px] text-red-600">{(regenSeo.error as Error)?.message}</p>
          )}

        </div>
      </div>

      {picking && (
        <SlotMediaPickerModal
          page={page}
          slot={slot}
          currentAssignment={
            current
              ? {
                  mediaAssetId: current.mediaAssetId,
                  alt: current.alt ?? null,
                  overlayOpacity: current.overlayOpacity ?? null,
                  orientation: (current.orientation ?? null) as
                    | "landscape"
                    | "square"
                    | "portrait"
                    | null,
                }
              : null
          }
          onAssign={async (vars) => {
            await upsert.mutateAsync(vars);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}
